/**
 * SEO Configuration & Strategy
 * Topic clusters, internal linking structure, and keyword mapping
 */

export const SEO_STRATEGY = {
  // Core topic clusters (pillar + supporting articles)
  clusters: {
    clia_cap_compliance: {
      pillar: {
        url: '/solutions',
        title: 'CLIA & CAP Compliance Consulting for Independent Labs',
        keywords: ['CLIA compliance consulting', 'CAP accreditation', 'CLIA gap audit', 'CAP inspection readiness', 'clinical lab compliance', 'CLIA deficiency citation', 'CLIA personnel competency'],
      },
      supporting: [
        { url: '/InspectionReadiness', title: 'Lab Inspection Readiness | CLIA & CAP Audit Preparation' },
        { url: '/lab-audit-preparation', title: 'Lab Audit Preparation Guide' },
        { url: '/Contact', title: 'Book a Free CLIA Gap Audit' },
        { url: '/ValidationIntake', title: 'Free Lab Compliance Assessment' },
      ]
    },
    capa_deviation: {
      pillar: {
        url: '/Solutions',
        title: 'CAPA & Deviation Documentation for CLIA Labs and Pharma',
        keywords: ['CAPA documentation', 'deviation report writing', 'CAPA CLIA lab', 'nonconformity handling', 'CAPA root cause analysis'],
      },
      supporting: [
        { url: '/blog/capa-root-cause-analysis', title: 'CAPA Root Cause Analysis: Methods & FDA Compliance' },
        { url: '/blog/hidden-cost-of-qa-documentation', title: 'Hidden Costs of Manual QA Documentation' },
        { url: '/blog/reduce-sop-writing-time', title: 'How to Reduce SOP Writing Time by 50%' },
        { url: '/ROICalculator', title: 'QA Documentation ROI Calculator' },
      ]
    },
    validation_protocols: {
      pillar: {
        url: '/qa-documentation-software',
        title: 'IQ/OQ/PQ Validation Documentation Software',
        keywords: ['validation protocol', 'IQ OQ PQ', 'equipment qualification', 'installation qualification', 'performance qualification'],
      },
      supporting: [
        { url: '/blog/iq-oq-pq-validation-guide', title: 'IQ OQ PQ Validation: A Practical Guide' },
        { url: '/LabAuditPreparation', title: 'Lab Audit Preparation Guide' },
        { url: '/InspectionReadiness', title: 'FDA Inspection Readiness Checklist' },
        { url: '/ValidationIntake', title: 'Free Validation Assessment' },
      ]
    },
    gxp_compliance: {
      pillar: {
        url: '/blog/gxp-compliance-guide-small-labs',
        title: 'GxP & CLIA Compliance Guide for Small Labs',
        keywords: ['GxP compliance', 'CLIA compliance small lab', 'lab SOP management', 'ALCOA+', 'FDA audit trail', 'FDA CSA', 'EU AI Act', '21 CFR Part 11'],
      },
      supporting: [
        { url: '/AITransparencyStatement', title: 'AI Transparency & GxP Compliance Statement' },
        { url: '/WhitePapers', title: 'Compliance & Regulatory White Papers' },
        { url: '/blog', title: 'Compliance Blog & Resources' },
        { url: '/Contact', title: 'Schedule Compliance Consultation' },
      ]
    },
    sop_development: {
      pillar: {
        url: '/sop-management-system',
        title: 'SOP Management & Development',
        keywords: ['SOP writing', 'SOP template', 'procedure documentation'],
      },
      supporting: [
        { url: '/DocumentGenerator', title: 'AI SOP Document Generator' },
        { url: '/SOPManagementSystem', title: 'SOP Management System' },
        { url: '/AdminManual', title: 'SOP Writing Manual & Templates' },
      ]
    }
  },

  // Internal linking priorities (high-authority pages that should link out)
  internalLinkingPriorities: {
    high: [
      { from: '/', to: ['/solutions', '/pricing', '/roi-calculator', '/validation-intake'] },
      { from: '/solutions', to: ['/qa-documentation-software', '/sop-management-system', '/lab-audit-preparation'] },
      { from: '/pricing', to: ['/roi-calculator', '/validation-intake', '/contact'] },
      { from: '/blog', to: ['/solutions', '/pricing', '/validation-intake'] },
    ],
    medium: [
      { from: '/lab-audit-preparation', to: ['/solutions/inspection-readiness', '/validation-intake'] },
      { from: '/WhitePapers', to: ['/AITransparencyStatement', '/contact'] },
    ]
  },

  // Keyword-to-URL mapping for content optimization
  keywordMapping: {
    'CLIA compliance consulting': { url: '/solutions', priority: 'high', intent: 'solution' },
    'CAP inspection readiness': { url: '/solutions/inspection-readiness', priority: 'high', intent: 'solution' },
    'CLIA gap audit': { url: '/contact', priority: 'high', intent: 'conversion' },
    'CLIA gap analysis': { url: '/solutions', priority: 'high', intent: 'solution' },
    'lab compliance audit': { url: '/solutions/inspection-readiness', priority: 'high', intent: 'solution' },
    'CLIA compliance small lab': { url: '/solutions', priority: 'high', intent: 'solution' },
    'CAPA CLIA lab': { url: '/solutions/capa-documentation', priority: 'high', intent: 'solution' },
    'lab SOP management': { url: '/sop-management-system', priority: 'high', intent: 'solution' },
    'CAPA documentation': { url: '/solutions/capa-documentation', priority: 'high', intent: 'solution' },
    'deviation report writing': { url: '/solutions/deviation-documentation', priority: 'high', intent: 'solution' },
    'IQ OQ PQ validation': { url: '/qa-documentation-software', priority: 'high', intent: 'solution' },
    'GxP compliance software': { url: '/qa-documentation-software', priority: 'high', intent: 'solution' },
    'SOP writing template': { url: '/sop-management-system', priority: 'high', intent: 'solution' },
    'lab audit preparation': { url: '/lab-audit-preparation', priority: 'high', intent: 'guide' },
    'FDA CSA compliance': { url: '/AITransparencyStatement', priority: 'medium', intent: 'guide' },
    'ALCOA+ documentation': { url: '/AITransparencyStatement', priority: 'medium', intent: 'guide' },
    'AI in QA documentation': { url: '/solutions', priority: 'medium', intent: 'guide' },
    'CLIA compliance ROI': { url: '/roi-calculator', priority: 'medium', intent: 'tool' },
  },

  // Page-specific SEO metadata (for rich snippets, breadcrumbs, FAQ schema)
  pageMetadata: {
    '/': {
      pageType: 'WebPage',
      breadcrumbs: [{ name: 'Home', url: 'https://labcomplyx.com/' }],
    },
    '/solutions': {
      pageType: 'WebPage',
      breadcrumbs: [
        { name: 'Home', url: 'https://labcomplyx.com/' },
        { name: 'Solutions', url: 'https://labcomplyx.com/solutions' }
      ],
    },
    '/qa-documentation-software': {
      pageType: 'WebPage',
      breadcrumbs: [
        { name: 'Home', url: 'https://labcomplyx.com/' },
        { name: 'Solutions', url: 'https://labcomplyx.com/solutions' },
        { name: 'QA Documentation Software', url: 'https://labcomplyx.com/qa-documentation-software' }
      ],
    },
    '/blog': {
      pageType: 'WebPage',
      breadcrumbs: [
        { name: 'Home', url: 'https://labcomplyx.com/' },
        { name: 'Blog', url: 'https://labcomplyx.com/blog' }
      ],
    },
  }
};

export const INTERNAL_LINK_STRATEGY = {
  /**
   * Get recommended internal links for a page URL
   * Returns links to related pages for better topical authority
   */
  getLinksForPage(pageUrl) {
    const baseUrl = 'https://labcomplyx.com';
    
    const linkMap = {
      '/': [
        { text: 'See Our Solutions', href: '/solutions', anchor: 'top services' },
        { text: 'Calculate Your ROI', href: '/roi-calculator', anchor: 'pricing impact' },
        { text: 'Book Free Assessment', href: '/validation-intake', anchor: 'start here' },
      ],
      '/solutions': [
        { text: 'QA Documentation Software', href: '/qa-documentation-software', anchor: 'core platform' },
        { text: 'SOP Management System', href: '/sop-management-system', anchor: 'related solution' },
        { text: 'Lab Audit Preparation', href: '/lab-audit-preparation', anchor: 'related solution' },
      ],
      '/qa-documentation-software': [
        { text: 'Explore All Solutions', href: '/solutions', anchor: 'all solutions' },
        { text: 'Lab Audit Readiness', href: '/lab-audit-preparation', anchor: 'related feature' },
        { text: 'View Pricing & ROI', href: '/roi-calculator', anchor: 'pricing' },
      ],
      '/pricing': [
        { text: 'Calculate Your ROI', href: '/roi-calculator', anchor: 'pricing' },
        { text: 'Book Assessment', href: '/validation-intake', anchor: 'get started' },
      ],
      '/sop-management-system': [
        { text: 'Back to Solutions', href: '/solutions', anchor: 'all solutions' },
        { text: 'QA Documentation Software', href: '/qa-documentation-software', anchor: 'related' },
      ],
      '/lab-audit-preparation': [
        { text: 'Inspection Readiness Checklist', href: '/solutions/inspection-readiness', anchor: 'next step' },
        { text: 'QA Documentation Software', href: '/qa-documentation-software', anchor: 'platform' },
      ],
      '/solutions/inspection-readiness': [
        { text: 'Lab Audit Preparation', href: '/lab-audit-preparation', anchor: 'full guide' },
        { text: 'Start Free Assessment', href: '/validation-intake', anchor: 'begin' },
      ],
      '/blog': [
        { text: 'Solutions Overview', href: '/solutions', anchor: 'explore' },
        { text: 'White Papers', href: '/WhitePapers', anchor: 'deeper dive' },
      ],
      '/WhitePapers': [
        { text: 'AI Transparency Statement', href: '/AITransparencyStatement', anchor: 'compliance' },
        { text: 'Blog & Resources', href: '/blog', anchor: 'articles' },
      ],
    };
    
    return linkMap[pageUrl] || [];
  }
};

export const FAQ_SCHEMA_PAGES = {
  '/': [
    {
      question: 'What services does LabComplyx offer?',
      answer: 'LabComplyx provides CLIA compliance consulting, CAP inspection readiness support, and AI-assisted QA documentation for independent clinical labs and pharma facilities. Core services include CLIA gap audits, CAPA documentation, deviation reporting, IQ/OQ/PQ validation protocols, SOP development, and lab compliance consulting — all with mandatory human review before any document enters use.'
    },
    {
      question: 'What is a CLIA gap audit and does my lab need one?',
      answer: 'A CLIA gap audit is a structured review of your lab\'s compliance posture against 42 CFR Part 493 requirements — covering personnel competency documentation, QC policies, proficiency testing, and procedure manuals. If your lab is due for a CLIA inspection or has received deficiency citations, a gap audit gives you a clear remediation roadmap before the inspector arrives. LabComplyx offers a free initial gap audit.'
    },
    {
      question: 'How can LabComplyx help prepare my lab for a CAP inspection?',
      answer: 'We conduct a 90-day CAP inspection readiness program covering checklist review against current CAP checklists, documentation gap remediation, personnel competency verification, QC policy alignment, and mock inspection walkthroughs.'
    },
    {
      question: 'What is the pricing for LabComplyx services?',
      answer: 'Engagements start at $1,500 for a Workflow Assessment (workflow audit, gap analysis, remediation roadmap). Pilot Workflows are priced by workflow area, starting from $5,500. Core Rollout (2–3 workflow areas) is $19,500; Full Rollout (4–5 workflow areas) is $27,500. Optional Fractional Compliance Support is available as an add-on. Monthly plans are seat-capped (3, 10, or 25 users) rather than charging per-seat add-on fees.'
    },
    {
      question: 'How long does GxP documentation implementation take?',
      answer: 'Most labs complete their Pilot Workflow in 4–6 weeks. The Pilot delivers measurable, documented results on real documents within the first supervised cycle, giving you evidence before committing to a larger rollout.'
    },
    {
      question: 'Are your services tailored for small labs and single-site pharma?',
      answer: 'Yes. LabComplyx specializes exclusively in small labs and single-site pharma/diagnostics facilities operating under ISO 13485, ISO 17025, FDA 21 CFR Part 820, 21 CFR Part 211, CLIA, CAP, and related standards — at consulting rates far below enterprise QMS platforms or full-time QA hires.'
    },
    {
      question: 'How much time can AI save on CAPA and deviation documentation?',
      answer: 'AI-assisted drafting produces a comprehensive first draft of a CAPA root cause investigation or deviation report in minutes rather than hours, which QA then reviews, edits, and approves. Actual time saved varies by document complexity and your team\'s review process — every document requires mandatory human review before entering the quality system.'
    },
    {
      question: 'Is AI-generated QA documentation FDA and GxP compliant?',
      answer: 'Every AI-generated document undergoes mandatory human-in-the-loop review before GMP use — AI assists, humans approve.'
    },
    {
      question: 'What is the difference between GxP and GMP compliance?',
      answer: 'GxP is an umbrella term covering all Good Practice (GP) guidelines in regulated industries — including GMP (manufacturing), GLP (laboratory), GCP (clinical), and GDP (distribution). LabComplyx supports documentation compliance across all GxP categories, with primary focus on GMP and GLP for labs and pharma facilities.'
    },
    {
      question: 'Do I need to purchase software or change my existing systems?',
      answer: 'No. LabComplyx is vendor-neutral and integrates with your existing LIMS (QBench, CloudLIMS, Labguru, LabArchives) and QMS (Veeva, MasterControl, TrackWise). We configure AI workflows using your existing platforms — no new software procurement or IT infrastructure changes required.'
    },
    {
      question: 'What is ALCOA+ and why does it matter for lab documentation?',
      answer: 'ALCOA+ is FDA\'s data integrity standard: Attributable, Legible, Contemporaneous, Original, Accurate — plus Complete, Consistent, Enduring, and Available.'
    }
  ],
  '/solutions': [
    {
      question: 'What is included in the LabComplyx Pilot Workflow?',
      answer: 'The Pilot Workflow (priced by workflow area, starting from $5,500, over 4–6 weeks) includes AI workflow configuration, 5–10 fully supervised document cycles with real QA documents, a results summary with time-saved metrics, and a clear decision point before committing to Core or Full Rollout.'
    },
    {
      question: 'How does CAPA documentation work with AI assistance?',
      answer: 'LabComplyx drafts CAPA root cause investigation narratives, corrective action plans, and effectiveness check summaries as a first draft. GxP guardrails enforce consistent ALCOA+ language and regulatory terminology. Human QA review is mandatory before any CAPA enters your quality system.'
    },
    {
      question: 'How does IQ OQ PQ validation protocol generation work?',
      answer: 'Our platform generates Installation Qualification (IQ), Operational Qualification (OQ), and Performance Qualification (PQ) protocol documentation from your equipment specifications as a structured first draft, while validation engineers review and sign off each section before GMP release.'
    },
    {
      question: 'What regulatory standards does LabComplyx support?',
      answer: 'LabComplyx supports documentation for: 21 CFR Part 11, 21 CFR Part 211, 21 CFR Part 820, ICH Q7, ICH Q10, ICH E6(R3), ISO 13485, ISO 17025, ISO 15189, CLIA, CAP, EPA SW-846, NELAC/TNI, EU GMP Annex 11, and EMA GCP guidelines. Coverage is continually updated as regulations evolve.'
    },
    {
      question: 'Do you charge per user or per seat?',
      answer: 'Monthly plans are seat-capped rather than billed per user — 2, 5, or 10 seats depending on plan tier, covering users, reviewers, and approvers within that cap. Project engagements (Pilot, Core, Full Rollout) are priced by workflow area, not by seat count.'
    },
    {
      question: 'How do LIMS integrations work with LabComplyx?',
      answer: 'We build secure API connections to your LIMS (QBench, CloudLIMS, Labguru, LabArchives, or custom).'
    },
    {
      question: 'Do I need to complete a pilot before a full rollout?',
      answer: 'A pilot is strongly recommended but not required. The Workflow Assessment ($1,500) and Pilot Workflow (from $5,500, priced by workflow area) are designed to prove ROI with real documents before committing to Core or Full Rollout. However, labs with clear needs can move directly to rollout.'
    }
  ],
  '/qa-documentation-software': [
    {
      question: 'What makes this software different from generic QMS tools?',
      answer: 'This platform is purpose-built for small labs and single-site pharma, with FDA CSA compliance, ALCOA+ alignment, and AI-assisted drafting.'
    },
    {
      question: 'Can I integrate with my existing QMS?',
      answer: 'Yes. The system exports to PDF, Word, and integrates with Veeva Vault, MasterControl, and other QMS platforms.'
    },
  ],
  '/LabAuditPreparation': [
    {
      question: 'What should I prepare before a lab audit?',
      answer: 'Ensure your QA documentation is complete, your audit trail is clean, all deviations have documented CAPAs, and your SOPs reflect current practices.'
    },
    {
      question: 'How can I improve my audit readiness score?',
      answer: 'Use our assessment tool to identify gaps, then prioritize documentation completeness, traceability, and compliance evidence.'
    },
  ]
};